import toPairs from "lodash/toPairs";
import denormalizeTaxNumber from "denormalize-steuernummer";
import finanzamt from "finanzamt";
import normalizeSteuernummer from "normalize-steuernummer";
import queryString from "query-string";

import { TaxNumberType } from "./types";
import { StateAbbreviation } from "./types";
import { IsMainBusinessTaxNumber } from "./types";

export const TAX_NUMBER_TYPE_LABELS: {
  [taxNumberType: string]: string;
} = {
  [TaxNumberType.PERSONAL]: "Private Steuernummer",
  [TaxNumberType.BUSINESS]: "Business Steuernummer",
};

export const TAX_NUMBER_OPTIONS = toPairs(TAX_NUMBER_TYPE_LABELS).map(
  ([taxNumberType, label]) => ({
    value: taxNumberType as TaxNumberType,
    label,
  })
);

export const STATE_LABEL: {
  [state: string]: string;
} = {
  [StateAbbreviation.DE_BW]: "Baden-Württemberg",
  [StateAbbreviation.DE_BY]: "Bayern",
  [StateAbbreviation.DE_BE]: "Berlin",
  [StateAbbreviation.DE_BB]: "Brandenburg",
  [StateAbbreviation.DE_HB]: "Bremen",
  [StateAbbreviation.DE_HH]: "Hamburg",
  [StateAbbreviation.DE_HE]: "Hessen",
  [StateAbbreviation.DE_MV]: "Mecklenburg-Vorpommern",
  [StateAbbreviation.DE_NI]: "Niedersachsen",
  [StateAbbreviation.DE_NW]: "Nordrhein-Westfalen",
  [StateAbbreviation.DE_RP]: "Rheinland-Pfalz",
  [StateAbbreviation.DE_SL]: "Saarland",
  [StateAbbreviation.DE_SN]: "Sachsen",
  [StateAbbreviation.DE_ST]: "Sachsen-Anhalt",
  [StateAbbreviation.DE_SH]: "Schleswig-Holstein",
  [StateAbbreviation.DE_TH]: "Thüringen",
};

export const STATE_OPTION = toPairs(STATE_LABEL).map(([state, label]) => ({
  value: state as StateAbbreviation,
  label,
}));

export const IS_MAIN_TAX_NUMBER_OPTIONS = [
  {
    label: IsMainBusinessTaxNumber.YES,
    value: true,
  },
  {
    label: IsMainBusinessTaxNumber.NO,
    value: false,
  },
];

export const TABLE_COLUMN_TITLES = {
  TYPE: "Art",
  STATE: "Bundesland",
  TAX_NUMBER: "Steuernummer",
  VALID_FROM: "Gültig ab",
  DESCRIPTION: "Info",
  ACTION: "Aktion",
  MAIN_TAX_NUMBER: "Hauptsteuernr.",
};

export const getTaxAccountViewPath = ({
  email,
  taxNumber: normalizedTaxNumber,
}: {
  email: string;
  taxNumber: string;
}) => {
  const { taxState, taxNumber } =
    denormalizeTaxNumber(normalizedTaxNumber)! || {};

  return {
    pathname: "/tax-account-view",
    search: queryString.stringify({
      email,
      taxNumber,
      state: taxState,
    }),
  };
};

export const NEW_TAX_NUMBER_RECORD_ID = "new";

/**
 * First digits of the normalized 13-digit Steuernummer per state (the
 * Bundesfinanzamtsnummer prefix). Exactly one prefix matches every Finanzamt.
 */
export const STATE_PREFIX: { [state in StateAbbreviation]: string } = {
  [StateAbbreviation.DE_BW]: "28",
  [StateAbbreviation.DE_BY]: "9",
  [StateAbbreviation.DE_BE]: "11",
  [StateAbbreviation.DE_BB]: "30",
  [StateAbbreviation.DE_HB]: "24",
  [StateAbbreviation.DE_HH]: "22",
  [StateAbbreviation.DE_HE]: "26",
  [StateAbbreviation.DE_MV]: "40",
  [StateAbbreviation.DE_NI]: "23",
  [StateAbbreviation.DE_NW]: "5",
  [StateAbbreviation.DE_RP]: "27",
  [StateAbbreviation.DE_SL]: "10",
  [StateAbbreviation.DE_SN]: "32",
  [StateAbbreviation.DE_ST]: "31",
  [StateAbbreviation.DE_SH]: "21",
  [StateAbbreviation.DE_TH]: "41",
};

/** Shape per state, with X placeholders (never a real number). */
export const TAX_NUMBER_EXAMPLE: { [state in StateAbbreviation]: string } = {
  [StateAbbreviation.DE_BW]: "XXXXX/XXXXX",
  [StateAbbreviation.DE_BY]: "XXX/XXX/XXXXX",
  [StateAbbreviation.DE_BE]: "XX/XXX/XXXXX",
  [StateAbbreviation.DE_BB]: "0XX/XXX/XXXXX",
  [StateAbbreviation.DE_HB]: "XX XXX XXXXX",
  [StateAbbreviation.DE_HH]: "XX/XXX/XXXXX",
  [StateAbbreviation.DE_HE]: "0XX XXX XXXXX",
  [StateAbbreviation.DE_MV]: "0XX/XXX/XXXXX",
  [StateAbbreviation.DE_NI]: "XX/XXX/XXXXX",
  [StateAbbreviation.DE_NW]: "XXX/XXXX/XXXX",
  [StateAbbreviation.DE_RP]: "XX/XXX/XXXXX",
  [StateAbbreviation.DE_SL]: "0XX/XXX/XXXXX",
  [StateAbbreviation.DE_SN]: "2XX/XXX/XXXXX",
  [StateAbbreviation.DE_ST]: "1XX/XXX/XXXXX",
  [StateAbbreviation.DE_SH]: "XX/XXX/XXXXX",
  [StateAbbreviation.DE_TH]: "1XX/XXX/XXXXX",
};

/**
 * Strips all Unicode whitespace (tab, no-break space, ...). normalize-
 * steuernummer only drops "/" and ASCII spaces, so validation and saving must
 * hand it this cleaned value.
 */
export const cleanTaxNumber = (rawTaxNumber: string) =>
  rawTaxNumber.replace(/\s/gu, "");

const STATES_WITH_10_DIGITS = new Set<StateAbbreviation>([
  StateAbbreviation.DE_BW,
  StateAbbreviation.DE_BE,
  StateAbbreviation.DE_HB,
  StateAbbreviation.DE_HH,
  StateAbbreviation.DE_NI,
  StateAbbreviation.DE_RP,
  StateAbbreviation.DE_SH,
]);

/** Digits a tax number has in the state's own notation (all others: 11). */
export const getExpectedDigitCount = (state: StateAbbreviation) =>
  STATES_WITH_10_DIGITS.has(state) ? 10 : 11;

export type TaxNumberError =
  | { type: "FORMAT"; digitCount?: number; expectedDigits?: number }
  | { type: "WRONG_STATE"; officeState: StateAbbreviation }
  | { type: "AMBIGUOUS_STATE"; candidateStates: StateAbbreviation[] }
  | { type: "NO_TAX_OFFICE" };

const getStateOfNormalized = (normalized: string) =>
  (Object.keys(STATE_PREFIX) as StateAbbreviation[]).find((candidate) =>
    normalized.startsWith(STATE_PREFIX[candidate])
  );

/**
 * When the selected state's reading fails or finds no Finanzamt: read the
 * number in all 16 states and collect the Bundesland of every Finanzamt found,
 * without the selected state, sorted by German label.
 */
const getOtherFittingStates = (
  cleaned: string,
  selected: StateAbbreviation
): StateAbbreviation[] => {
  const states = new Set<StateAbbreviation>();
  (Object.keys(STATE_PREFIX) as StateAbbreviation[]).forEach((candidate) => {
    try {
      const normalized = normalizeSteuernummer(cleaned, candidate);
      const officeState = getStateOfNormalized(normalized);
      if (officeState && finanzamt(normalized)) {
        states.add(officeState);
      }
    } catch {
      // This state's notation does not fit the number.
    }
  });
  states.delete(selected);
  return Array.from(states).sort((a, b) =>
    STATE_LABEL[a].localeCompare(STATE_LABEL[b], "de")
  );
};

/** Errors that only warn: the operator may confirm and save anyway. */
export const isTaxNumberWarning = (error: TaxNumberError | null) =>
  error?.type === "NO_TAX_OFFICE" || error?.type === "AMBIGUOUS_STATE";

/**
 * Mirrors the WebApp check. Order: FORMAT, WRONG_STATE, NO_TAX_OFFICE.
 * NO_TAX_OFFICE is only a warning for the caller (the Finanzamt data set can
 * be outdated); FORMAT and WRONG_STATE block saving. A FORMAT or NO_TAX_OFFICE
 * result becomes WRONG_STATE when only one other Bundesland fits the number.
 * When several do, NO_TAX_OFFICE becomes AMBIGUOUS_STATE (a warning, too), but
 * FORMAT stays: a number the selected state cannot read cannot be saved there.
 */
export const getTaxNumberError = (
  rawTaxNumber: string,
  state: StateAbbreviation
): TaxNumberError | null => {
  const cleaned = cleanTaxNumber(rawTaxNumber);
  const withOtherState = (fallback: TaxNumberError): TaxNumberError => {
    const others = getOtherFittingStates(cleaned, state);
    if (others.length === 1) {
      return { type: "WRONG_STATE", officeState: others[0] };
    }
    return others.length > 1 && fallback.type === "NO_TAX_OFFICE"
      ? { type: "AMBIGUOUS_STATE", candidateStates: others }
      : fallback;
  };

  let normalized: string;
  try {
    normalized = normalizeSteuernummer(cleaned, state);
  } catch {
    const digitCount = rawTaxNumber.replace(/\D/g, "").length;
    const expectedDigits = getExpectedDigitCount(state);
    // Only name the count when it is what is wrong; otherwise the shape is.
    return withOtherState(
      digitCount !== expectedDigits
        ? { type: "FORMAT", digitCount, expectedDigits }
        : { type: "FORMAT" }
    );
  }

  if (!normalized.startsWith(STATE_PREFIX[state])) {
    const officeState = getStateOfNormalized(normalized);
    if (officeState) {
      return { type: "WRONG_STATE", officeState };
    }
  }

  if (!finanzamt(normalized)) {
    return withOtherState({ type: "NO_TAX_OFFICE" });
  }

  return null;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const getTaxNumberErrorMessage = (
  t: Translate,
  error: TaxNumberError,
  selectedState: StateAbbreviation
): string => {
  switch (error.type) {
    case "FORMAT":
      if (error.digitCount !== undefined) {
        return t("errors.formatDigitCount", {
          count: error.digitCount,
          state: STATE_LABEL[selectedState],
          expected: error.expectedDigits,
          example: TAX_NUMBER_EXAMPLE[selectedState],
        });
      }
      return t("errors.format", {
        state: STATE_LABEL[selectedState],
        example: TAX_NUMBER_EXAMPLE[selectedState],
      });
    case "WRONG_STATE":
      return t("errors.wrongState", {
        officeState: STATE_LABEL[error.officeState],
        selectedState: STATE_LABEL[selectedState],
      });
    case "AMBIGUOUS_STATE": {
      const labels = error.candidateStates.map((c) => STATE_LABEL[c]);
      const last = labels[labels.length - 1];
      const states =
        labels.length > 1
          ? `${labels.slice(0, -1).join(", ")} ${t("errors.listAnd")} ${last}`
          : last;
      return t("errors.ambiguousState", {
        selectedState: STATE_LABEL[selectedState],
        states,
      });
    }
    case "NO_TAX_OFFICE":
      return t("errors.noTaxOffice");
  }
};
